// === supabaseClassroomAPI.js ===
import { supabase } from '../supabaseClient';

// ---------- Utilities ----------
const CODE_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

function randomCode(len = 6) {
  return Array.from({ length: len }, () =>
    CODE_ALPHABET[Math.floor(Math.random() * CODE_ALPHABET.length)]
  ).join('');
}

function normalizeCode(code) {
  return (code || '').trim().toUpperCase();
}

function isValidCode(code) {
  return /^[A-Z0-9]{6}$/.test(code);
}

// ---------- Create classroom (teacher only) ----------
export async function createClassroom(name, userId) {
  if (!userId) throw new Error('Missing userId.');
  if (!name || !name.trim()) throw new Error('Classroom name is required.');

  // Try to avoid code collisions (in case of high volume)
  let attempt = 0;
  while (attempt < 5) {
    const code = randomCode(6);
    // Check if code exists
    const { data: exists, error: existsErr } = await supabase
      .from('classrooms')
      .select('id')
      .eq('code', code)
      .maybeSingle();

    if (existsErr) throw existsErr;
    if (!exists) {
      // Create with this unique code
      const { data, error } = await supabase
        .from('classrooms')
        .insert([{ name: name.trim(), host_id: userId, code }])
        .select('id, name, code, host_id')
        .single();
      if (error) throw error;
      return data;
    }

    attempt += 1;
  }
  throw new Error('Could not generate a unique join code. Please try again.');
}

// ---------- Join classroom (student) ----------
export async function joinClassroomByCode(code, userId) {
  if (!userId) throw new Error('Missing userId.');
  const normalized = normalizeCode(code);
  if (!isValidCode(normalized)) throw new Error('Invalid code.');

  // Find classroom
  const { data: classroom, error: lookupError } = await supabase
    .from('classrooms')
    .select('id, name, code, host_id')
    .eq('code', normalized)
    .maybeSingle();

  if (lookupError) throw lookupError;
  if (!classroom) throw new Error('Classroom not found.');

  // Already a member?
  const { data: existing, error: existingError } = await supabase
    .from('classroom_members')
    .select('id, role')
    .eq('classroom_id', classroom.id)
    .eq('user_id', userId)
    .maybeSingle();

  if (existingError) throw existingError;

  // If existing, just return the classroom; do not error out
  if (existing) return classroom;

  // Insert membership
  const { error: insertError } = await supabase
    .from('classroom_members')
    .insert([{ classroom_id: classroom.id, user_id: userId, role: 'student' }]);

  if (insertError) throw insertError;

  return classroom;
}

// ---------- Get classrooms for current user (joined/owned) ----------
export async function getClassroomsForUser(userId) {
  if (!userId) throw new Error('Missing userId.');

  const { data, error } = await supabase
    .from('classroom_members')
    .select(`
      role,
      classroom:classrooms (
        id,
        name,
        code,
        host_id
      )
    `)
    .eq('user_id', userId);

  if (error) throw error;

  return (data || []).map((row) => ({
    ...row.classroom,
    role: row.role,
  }));
}

// ---------- Get classrooms owned by current user (teacher) ----------
export async function getOwnedClassrooms(userId) {
  if (!userId) throw new Error('Missing userId.');
  const { data, error } = await supabase
    .from('classrooms')
    .select('id, name, code, host_id')
    .eq('host_id', userId);

  if (error) throw error;
  return data || [];
}

// ---------- List members of a classroom ----------
export async function getClassroomMembers(classroomId) {
  if (!classroomId) throw new Error('Missing classroomId.');
  const { data, error } = await supabase
    .from('classroom_members')
    .select(`
      user_id,
      role,
      profiles (
        id,
        username,
        role
      )
    `)
    .eq('classroom_id', classroomId);

  if (error) throw error;
  return data || [];
}

// ---------- Get lessons attached to a classroom ----------
export async function getLessonsForClassroom(classroomId) {
  if (!classroomId) throw new Error('Missing classroomId.');
  const { data, error } = await supabase
    .from('classroom_lessons')
    .select('lesson:lessons (id, title, level)')
    .eq('classroom_id', classroomId);

  if (error) throw error;
  return (data || []).map((row) => row.lesson);
}

// ---------- Add lesson to classroom (host only; optional uniqueness guard) ----------
export async function addLessonToClassroom(classroomId, lessonId) {
  if (!classroomId || !lessonId) throw new Error('Missing classroomId or lessonId.');

  // If you added a unique index on (classroom_id, lesson_id) this will auto-prevent dupes.
  const { data, error } = await supabase
    .from('classroom_lessons')
    .insert([{ classroom_id: classroomId, lesson_id: lessonId }])
    .select();

  if (error) throw error;
  return data;
}

// ---------- Leave classroom (student) ----------
export async function leaveClassroom(classroomId, userId) {
  if (!classroomId || !userId) throw new Error('Missing classroomId or userId.');
  const { error } = await supabase
    .from('classroom_members')
    .delete()
    .eq('classroom_id', classroomId)
    .eq('user_id', userId);

  if (error) throw error;
  return true;
}
